import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { trackProviderViewed } from '@/lib/analytics-events'
import { Link, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { ExternalLink } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { api } from '@/lib/api'
import {
  IncidentCard,
  type SupplyChainIncident,
} from '@/components/supply-chain/IncidentCard'

/**
 * Surface 3 of the supply-chain tracker (Plan C Plan 2 T19).
 *
 * Provider detail at `/providers/:slug`. Two queries:
 *   - `GET /api/providers/:slug` → header + 90d severity histogram + 30d
 *     source-counts map.
 *   - `GET /api/providers/:slug/incidents?days=90` → paginated timeline,
 *     rendered through the shared `IncidentCard`.
 *
 * The timeline endpoint returns the same incident shape as
 * `/api/supply-chain/incidents` (provider fields inlined), so `IncidentCard`
 * can be reused without remapping.
 */

interface ProviderDetail {
  id: string
  slug: string
  name: string
  category: 'cloud' | 'identity'
  homepageUrl: string | null
  statusPageUrl: string | null
  bulletinUrl: string | null
  description: string | null
  active: boolean
  stats: {
    last30d: number
    last90d: number
    lastCriticalAt: string | null
  }
  severityDistributionLast90d: {
    critical: number
    high: number
    medium: number
    low: number
    unknown: number
  }
  recentIncidentSourceCounts: Record<string, number>
}

interface IncidentsResponse {
  incidents: SupplyChainIncident[]
  total: number
  page: number
  pageSize: number
}

export function ProviderDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>()

  // KAN-32 provider:viewed
  useEffect(() => {
    trackProviderViewed(slug)
  }, [slug])

  const detailQuery = useQuery({
    queryKey: ['providers', 'detail', slug],
    queryFn: () => api.get<ProviderDetail>(`/providers/${slug}`),
    enabled: Boolean(slug),
    retry: false,
  })

  const incidentsQuery = useQuery({
    queryKey: ['providers', 'incidents', slug, 90],
    queryFn: () =>
      api.get<IncidentsResponse>(`/providers/${slug}/incidents?days=90`),
    enabled: Boolean(slug),
    retry: false,
  })

  if (detailQuery.isLoading) {
    return (
      <p className="p-8 text-sm text-muted-foreground">Loading provider…</p>
    )
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="p-8">
        <h1 className="text-lg font-semibold">Provider not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          <Link
            to="/threats/supply-chain?tab=providers"
            className="text-primary hover:underline"
          >
            Back to providers.
          </Link>
        </p>
      </div>
    )
  }

  const provider = detailQuery.data
  const incidents = incidentsQuery.data?.incidents ?? []
  const sevDist = provider.severityDistributionLast90d
  const sourceCounts = Object.entries(provider.recentIncidentSourceCounts).sort(
    (a, b) => b[1] - a[1],
  )

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link
            to="/threats/supply-chain?tab=providers"
            className="hover:underline"
          >
            Providers
          </Link>
          <span aria-hidden>/</span>
          <span className="text-foreground">{provider.name}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {provider.name}
          </h1>
          <Badge variant="outline" className="capitalize">
            {provider.category}
          </Badge>
          {!provider.active && (
            <Badge variant="outline">deprecated</Badge>
          )}
        </div>
        {provider.description && (
          <p className="text-sm text-muted-foreground">
            {provider.description}
          </p>
        )}
        <div className="flex flex-wrap gap-3 text-sm">
          {provider.homepageUrl && (
            <ProviderLink label="Homepage" url={provider.homepageUrl} />
          )}
          {provider.statusPageUrl && (
            <ProviderLink label="Status page" url={provider.statusPageUrl} />
          )}
          {provider.bulletinUrl && (
            <ProviderLink
              label="Security bulletins"
              url={provider.bulletinUrl}
            />
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile label="Last 30 days" value={provider.stats.last30d} />
        <StatTile label="Last 90 days" value={provider.stats.last90d} />
        <StatTile
          label="Last critical"
          value={
            provider.stats.lastCriticalAt
              ? format(
                  new Date(provider.stats.lastCriticalAt),
                  'yyyy-MM-dd',
                )
              : '—'
          }
        />
      </div>

      <Card aria-labelledby="provider-severity-title">
        <CardHeader>
          <CardTitle id="provider-severity-title" className="text-base">
            Severity distribution (90d)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SeverityHistogram distribution={sevDist} />
        </CardContent>
      </Card>

      {sourceCounts.length > 0 && (
        <Card aria-labelledby="provider-sources-title">
          <CardHeader>
            <CardTitle id="provider-sources-title" className="text-base">
              Sources (30d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-wrap gap-2 text-sm">
              {sourceCounts.map(([source, count]) => (
                <li key={source}>
                  <Badge variant="secondary" className="font-mono">
                    {source}{' '}
                    <span className="ml-1 tabular-nums text-muted-foreground">
                      {count}
                    </span>
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <section
        aria-labelledby="provider-incidents-title"
        className="space-y-3"
      >
        <h2
          id="provider-incidents-title"
          className="text-lg font-semibold tracking-tight"
        >
          Incident timeline (90d)
        </h2>
        {incidentsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading incidents…</p>
        ) : incidentsQuery.isError ? (
          <p className="text-sm text-muted-foreground">
            Incident timeline unavailable.
          </p>
        ) : incidents.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No incidents in the last 90 days.
          </p>
        ) : (
          <div className="space-y-3">
            {incidents.map((i) => (
              <IncidentCard key={i.id} incident={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function ProviderLink({ label, url }: { label: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:underline"
    >
      {label} <ExternalLink className="h-3 w-3" aria-hidden />
    </a>
  )
}

function StatTile({
  label,
  value,
}: {
  label: string
  value: string | number
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

function SeverityHistogram({
  distribution,
}: {
  distribution: ProviderDetail['severityDistributionLast90d']
}) {
  const buckets: Array<{ key: keyof ProviderDetail['severityDistributionLast90d']; label: string; barClass: string }> = [
    { key: 'critical', label: 'Critical', barClass: 'bg-severity-critical' },
    { key: 'high', label: 'High', barClass: 'bg-severity-high' },
    { key: 'medium', label: 'Medium', barClass: 'bg-severity-medium' },
    { key: 'low', label: 'Low', barClass: 'bg-severity-low' },
    { key: 'unknown', label: 'Unknown', barClass: 'bg-muted-foreground' },
  ]
  const max = Math.max(...buckets.map((b) => distribution[b.key]), 1)
  const total = buckets.reduce((acc, b) => acc + distribution[b.key], 0)

  if (total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No incidents in the last 90 days.
      </p>
    )
  }

  return (
    <ul className="space-y-2">
      {buckets.map((b) => {
        const count = distribution[b.key]
        const widthPct = (count / max) * 100
        return (
          <li key={b.key} className="flex items-center gap-3 text-sm">
            <span className="w-20 text-xs uppercase tracking-wide text-muted-foreground">
              {b.label}
            </span>
            <span className="h-2 flex-1 overflow-hidden rounded bg-muted">
              <span
                className={`block h-full ${b.barClass}`}
                style={{ width: `${widthPct}%` }}
                aria-hidden
              />
            </span>
            <span className="w-10 text-right font-medium tabular-nums">
              {count}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

export default ProviderDetailPage
